import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import { useMutationState } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowLeftIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { AllocationImagesPool } from '@/features/photoset/components/allocation-images-pool'
import {
	PhotosetStepGridContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { RejectPhotosetModal } from '@/features/photoset/components/reject-photoset-modal'
import { SpacesSidebar } from '@/features/photoset/components/spaces-sidebar'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { useAllocationShortcuts } from '@/features/photoset/hooks/use-allocation-shortcuts.tsx'
import { useExpandPhotosetImage } from '@/features/photoset/hooks/use-expand-photoset-image'
import { useAllocateImages } from '@/features/photoset/queries/use-allocate-images'
import { useDeallocateImages } from '@/features/photoset/queries/use-deallocate-images'
import { useGetPhotosetImagesList } from '@/features/photoset/queries/use-get-photoset-images-list'
import { useGetPhotosetSpaces } from '@/features/photoset/queries/use-get-photoset-spaces'
import { useGetReasonsDecline } from '@/features/photoset/queries/use-get-reasons-decline'
import { useUpdateStepToInitialQA } from '@/features/photoset/queries/use-update-step-to-initial-qa'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import type { PhotosetURLParams } from '@/features/photoset/types.ts'
import { generateToastAllocationId } from '@/features/photoset/utils.ts'
import { errorHandler } from '@/helpers/utils'
import { MUTATION_KEYS, QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetAllocationStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const [rejectModal, showRejectModal] = useState(false)
	const initSpaces = usePhotosetStore((state) => state.initSpaces)
	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const deallocateImages = usePhotosetStore((state) => state.deallocateImages)
	const unselectAll = usePhotosetStore((state) => state.unselectAll)
	const [currentSpaceIndex, setCurrentSpaceIndex] = useState<number | null>(
		null
	)

	//queries
	const imagesQuery = useGetPhotosetImagesList(
		{ urlParams: { photosetId: urlParams.id } },
		{ enabled: false }
	)
	const reasonsDeclineQuery = useGetReasonsDecline()

	//mutations
	const deallocateImagesMutation = useDeallocateImages()
	const allocateImagesMutation = useAllocateImages({
		options: { onError: (error) => errorHandler(error) },
		onUndo: async (imagesIds, photosetSpaceId) => {
			try {
				await deallocateImagesMutation.mutateAsync({
					urlParams: { photosetId: urlParams.id, photosetSpaceId },
					payload: imagesIds
				})

				const toastId = generateToastAllocationId(
					'allocation',
					photosetSpaceId,
					imagesIds
				)
				deallocateImages({
					imagesIds,
					photosetSpaceId,
					keepSelected: false,
					trackInHistory: false
				})
				toast.dismiss(toastId)
			} catch (error) {
				errorHandler(error)
			}
		}
	})

	const photosetSpacesQuery = useGetPhotosetSpaces({
		urlParams: { photosetId: urlParams.id },
		params: { response_type: 'extended' }
	})

	const updateStepToInitialQAMutation = useUpdateStepToInitialQA({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			unselectAll()
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
		}
	})

	// Get the update step to curation status even if it is invoked outside
	const updateStepToCurationStatus = useMutationState({
		filters: { mutationKey: [MUTATION_KEYS.UPDATE_STEP_CURATION] },
		select: (mutation) => mutation.state.status
	})

	useEffect(() => {
		initSpaces(photosetSpacesQuery.data ? photosetSpacesQuery.data : [])
	}, [photosetSpacesQuery.data, initSpaces])

	const { currentDetailImageIndex, setCurrentDetailImageIndex } =
		useExpandPhotosetImage()

	useAllocationShortcuts({
		disabled: currentSpaceIndex !== null || currentDetailImageIndex !== null
	})

	const onDragEnd = (event: DragEndEvent) => {
		if (event.over?.id) {
			allocateImagesMutation.mutate({
				urlParams: {
					photosetId: urlParams.id,
					photosetSpaceId: Number(event.over.id)
				},
				payload: selectedImages
			})
		}
	}

	return (
		<>
			<PageHero
				endSlot={<StepHeroControls />}
				subtitle={t('photoset:allocation.subtitle')}
				title={t('photoset:allocation.title')}
			/>
			<PhotosetStepWrapper>
				<PhotosetStepGridContainer>
					<DndContext onDragEnd={onDragEnd}>
						<AllocationImagesPool
							currentDetailImageIndex={currentDetailImageIndex}
							setCurrentDetailImageIndex={
								setCurrentDetailImageIndex
							}
						/>
						<Separator orientation='vertical' />
						<SpacesSidebar
							currentSpaceIndex={currentSpaceIndex}
							queryStatuses={[
								imagesQuery.status,
								photosetSpacesQuery.status
							]}
							setCurrentSpaceIndex={setCurrentSpaceIndex}
						/>
					</DndContext>
				</PhotosetStepGridContainer>
				<PageFooter
					centerSlot={
						<p>{t('photoset:allocation.footer_step_label')}</p>
					}
					startSlot={
						<div className='flex gap-4'>
							<ButtonLegacy
								disabled={
									updateStepToInitialQAMutation.isPending
								}
								onClick={() =>
									updateStepToInitialQAMutation.mutate(
										urlParams.id
									)
								}
								type='button'
								variant='tertiary'
							>
								<ArrowLeftIcon />
								{t('common:back')}
							</ButtonLegacy>
							<ButtonLegacy
								disabled={
									reasonsDeclineQuery.isFetching ||
									updateStepToInitialQAMutation.isPending ||
									updateStepToCurationStatus.includes(
										'pending'
									)
								}
								onClick={() => showRejectModal(true)}
								type='button'
								variant='tertiary'
							>
								{t('common:needs_reupload')}
							</ButtonLegacy>
						</div>
					}
				/>

				{rejectModal && reasonsDeclineQuery.data && (
					<RejectPhotosetModal
						close={() => showRejectModal(false)}
						reasons={reasonsDeclineQuery.data}
					/>
				)}
			</PhotosetStepWrapper>
		</>
	)
}

PhotosetAllocationStep.displayName = 'PhotosetAllocationStep'
